import { useEffect, useState } from "react";
import axios from "axios";
import { Check, Link2, Loader2, Plug, Search } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import McpServers, { waarschuw } from "./McpServers";
import RechtenPerApp from "./RechtenPerApp";
import ConnectorToevoegen from "./ConnectorToevoegen";
import { magKoppelen } from "./NextcloudConnection";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";
import { ALLE, CATALOGUS, EIGEN_ADRES, GEKOPPELD, NIET_KOPPELBAAR, VERLOPEN, eigenAdres, filterOp, ingebouwdeStatus, itemGekoppeld,
  uitBackend, zoekIn } from "@/lib/koppelcatalogus";
import { INLOGGEN, koppelStatus } from "@/lib/koppelstatus";

/**
 * W1a: het scherm Koppelingen, als groot venster vanuit het menu (besluit Frank
 * 23 oktober 2026: App.js valt onder de bevroren verbindingscode, dus geen route).
 *
 * Twee tabbladen en een zoekveld. Jouw koppelingen is het bestaande MCP-blok uit
 * Instellingen (`McpServers`), hier met het zoekveld erop; Instellingen zelf blijft
 * ongewijzigd. Ontdekken toont de catalogus, met als eerste Direkto. Koppelen vanuit
 * Ontdekken gaat langs dezelfde route als het formulier, en een kijker krijgt geen knop
 * (de backend weigert hem ook).
 */
//: CB1: de woorden bij inloggen en rechten, als vertaalsleutel.
const INLOG_TEKST = { oauth: "Inloggen bij de aanbieder", sleutel: "Met een sleutel", geen: "Zonder inlog", onbekend: "Inloggen onbekend" };
const RECHTEN_TEKST = { alleen_lezen: "Alleen lezen", lezen_schrijven: "Lezen en schrijven", onbekend: "Rechten onbekend" };
//: Glasronde 0.9.243 punt 1a en 1b.
const STATUS_TEKST = { gekoppeld: "Gekoppeld", verlopen: "Verlopen", niet_gekoppeld: "Niet gekoppeld" };
const SOORT_TEKST = { vast: "Vast adres", eigen_adres: "Met eigen adres", niet_koppelbaar: "Niet koppelbaar" };

export default function KoppelingenVenster({ open, onOpenChange }) {
  const { t, taal } = useTaal();
  // CB1: de lijst uit de backend; tot die er is (of bij een oudere backend) de ingebouwde lijst.
  const [catalogus, setCatalogus] = useState(CATALOGUS);
  const [filters, setFilters] = useState({ inloggen: ALLE, rechten: ALLE, zeker: ALLE, soort: ALLE });
  const [verbindingen, setVerbindingen] = useState([]);
  const [adressen, setAdressen] = useState({});
  const [tab, setTab] = useState("jouw");
  const [zoek, setZoek] = useState("");
  const [servers, setServers] = useState([]);
  const [rol, setRol] = useState("");
  const [bezig, setBezig] = useState("");
  const [tokens, setTokens] = useState({});

  useEffect(() => {
    if (!open || tab !== "ontdekken") return;
    axios.get(`${API}/mcp-servers`).then((r) => setServers(Array.isArray(r.data) ? r.data : [])).catch(() => setServers([]));
    axios.get(`${API}/organisatie`).then((o) => setRol(o.data?.mijn_rol || "")).catch(() => setRol(""));
    // Glasronde 0.9.243 punt 1a: de status van de ingebouwde koppelingen, uit dezelfde route als Instellingen.
    axios.get(`${API}/connections`)
      .then((r) => setVerbindingen(Array.isArray(r?.data?.koppelingen) ? r.data.koppelingen : []))
      .catch(() => setVerbindingen([]));
    axios.get(`${API}/mcp-servers/catalogus`)
      .then((r) => { const lijst = uitBackend(r?.data, taal); if (lijst) setCatalogus(lijst); })
      .catch(() => {});
  }, [open, tab, taal]);

  // CB2 (GO Frank 4 nov): na het koppelen meteen toetsen, en vraagt de server OAuth, meteen de inlog openen.
  // Lukt een van die stappen niet, dan staat de koppeling er wel en zegt Jouw koppelingen wat er mist.
  const toetsEnLogIn = async (item, server) => {
    try {
      await axios.post(`${API}/mcp-servers/${server.id}/toets`);
      return;
    } catch {
      // de status staat nu op de server; hieronder gelezen
    }
    try {
      const lijst = (await axios.get(`${API}/mcp-servers`))?.data;
      const nu = Array.isArray(lijst) ? lijst.find((s) => s.id === server.id) : null;
      if (item.inloggen !== "oauth" || koppelStatus(nu) !== INLOGGEN) return;
      const start = await axios.post(`${API}/mcp-servers/${server.id}/inloggen/start`);
      window.open(start.data.url, "_blank", "noopener");
      await axios.post(`${API}/mcp-servers/${server.id}/inloggen/klaar/${start.data.id}`);
      await axios.post(`${API}/mcp-servers/${server.id}/toets`);
    } catch {
      // Jouw koppelingen toont de status en de knop Inloggen; er gaat niets verloren.
    }
  };

  const koppel = async (item) => {
    // W4: een koppeling die een token vraagt, koppelt pas met een ingevuld token.
    const sleutel = item.sleutelNodig ? (tokens[item.id] || "").trim() : "";
    // Glasronde 0.9.243 punt 1b: een dienst met eigen adres koppelt pas met het adres van de eigen server.
    const adres = item.soort === EIGEN_ADRES ? eigenAdres(adressen[item.id], item.pad) : item.adres;
    if (!adres) {
      toast.error(t("Vul eerst het adres van je eigen server in."));
      return;
    }
    if (item.sleutelNodig && !sleutel) {
      toast.error(t("Vul eerst je token in."));
      return;
    }
    setBezig(item.id);
    try {
      // Plan b: een verkende server zonder naam krijgt de naam uit het adres (de backend, W1b).
      const r = await axios.put(`${API}/mcp-servers`, { naam: item.naam, adres, sleutel });
      setTokens((v) => ({ ...v, [item.id]: "" }));
      setAdressen((v) => ({ ...v, [item.id]: "" }));
      if (r?.data?.id) await toetsEnLogIn(item, r.data);
      toast.success(t("{naam} gekoppeld", { naam: r?.data?.naam || item.naam }));
      waarschuw(r?.data, t);
      setTab("jouw");
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Dat lukte niet. Er is niets gewijzigd."));
    } finally {
      setBezig("");
    }
  };

  const gevonden = zoekIn(filterOp(catalogus, filters), zoek, [(i) => i.naam, (i) => i.adres, (i) => t(i.omschrijving)]);
  const kies = (sleutel) => (e) => setFilters((f) => ({ ...f, [sleutel]: e.target.value }));
  const ingebouwd = zoekIn(ingebouwdeStatus(verbindingen, servers), zoek, [(i) => i.naam]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl h-[85dvh] flex flex-col" data-testid="koppelingen-venster">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Plug className="h-4 w-4" />{t("menu.koppelingen")}</DialogTitle>
          <DialogDescription>{t("Servers die gereedschap geven aan de chat en aan je agents. Lezen en zoeken gaat direct; bestellen, een link maken of iets aanvragen wacht eerst op je bevestiging.")}</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={zoek} onChange={(e) => setZoek(e.target.value)} className="pl-8"
                 placeholder={t("Zoek een koppeling")} aria-label={t("Zoek een koppeling")}
                 data-testid="koppelingen-zoek" />
        </div>
        <Tabs value={tab} onValueChange={setTab} className="flex-1 min-h-0 flex flex-col">
          <TabsList className="self-start">
            <TabsTrigger value="jouw" data-testid="koppelingen-tab-jouw">{t("Jouw koppelingen")}</TabsTrigger>
            <TabsTrigger value="ontdekken" data-testid="koppelingen-tab-ontdekken">{t("Ontdekken")}</TabsTrigger>
          </TabsList>
          <TabsContent value="jouw" className="flex-1 min-h-0 overflow-y-auto">
            <McpServers zoek={zoek} />
            <RechtenPerApp />
          </TabsContent>
          <TabsContent value="ontdekken" className="flex-1 min-h-0 overflow-y-auto">
            {/* CB1: filteren op inloggen, rechten en zekerheid. */}
            <div className="flex flex-wrap gap-2 mb-2 text-xs" data-testid="ontdekken-filters">
              <select value={filters.inloggen} onChange={kies("inloggen")} aria-label={t("Inloggen")}
                      className="border rounded p-1 bg-background" data-testid="filter-inloggen">
                <option value={ALLE}>{t("Elke inlog")}</option>
                {Object.entries(INLOG_TEKST).map(([k, v]) => <option key={k} value={k}>{t(v)}</option>)}
              </select>
              <select value={filters.rechten} onChange={kies("rechten")} aria-label={t("Rechten")}
                      className="border rounded p-1 bg-background" data-testid="filter-rechten">
                <option value={ALLE}>{t("Elke rechten")}</option>
                {Object.entries(RECHTEN_TEKST).map(([k, v]) => <option key={k} value={k}>{t(v)}</option>)}
              </select>
              <select value={filters.zeker} onChange={kies("zeker")} aria-label={t("Zekerheid")}
                      className="border rounded p-1 bg-background" data-testid="filter-zeker">
                <option value={ALLE}>{t("Zeker en onzeker")}</option>
                <option value="zeker">{t("Alleen zeker")}</option>
                <option value="onzeker">{t("Alleen onzeker")}</option>
              </select>
              <select value={filters.soort} onChange={kies("soort")} aria-label={t("Soort adres")}
                      className="border rounded p-1 bg-background" data-testid="filter-soort">
                <option value={ALLE}>{t("Elk adres")}</option>
                {Object.entries(SOORT_TEKST).map(([k, v]) => <option key={k} value={k}>{t(v)}</option>)}
              </select>
            </div>
            {/* Plan b (GO Frank 5 nov): een eigen adres verkennen; koppelen alleen op de klik. */}
            {magKoppelen(rol) && (
              <ConnectorToevoegen onKoppel={koppel} bezig={bezig} token={tokens.verkend}
                                  onToken={(w) => setTokens((v) => ({ ...v, verkend: w }))} />
            )}
            {/* Glasronde 0.9.243 punt 1a: wat NovaMund zelf meebrengt, met de status. Koppelen gaat in Instellingen. */}
            {ingebouwd.length > 0 && (
              <section className="mb-3" data-testid="ontdekken-ingebouwd">
                <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">{t("Ingebouwd in NovaMund")}</h3>
                <p className="text-xs text-muted-foreground mb-2">{t("Koppelen of opnieuw koppelen doe je in Instellingen, onder Verbindingen.")}</p>
                {/* Punt 1 (GO Frank 5 nov 2026): de weg naar een tweede Google-account. */}
                <p className="text-xs text-muted-foreground mb-2" data-testid="koppelingen-account-toevoegen">
                  {t("Nog een Google-account koppelen: Instellingen › Verbindingen › Account toevoegen.")}
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                  {ingebouwd.map((i) => (
                    <li key={i.id} className="border rounded-md px-3 py-2 flex items-center justify-between text-sm"
                        data-testid={`ingebouwd-${i.id}`}>
                      <span>{i.naam}</span>
                      <span className={`text-xs flex items-center gap-1 ${i.status === GEKOPPELD ? "" : i.status === VERLOPEN ? "text-[hsl(var(--destructive))]" : "text-muted-foreground"}`}
                            data-testid="ingebouwd-status">
                        {i.status === GEKOPPELD && <Check className="h-3 w-3" />}{t(STATUS_TEKST[i.status])}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {gevonden.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="ontdekken-leeg">
                {t("Geen koppeling met \"{zoek}\"", { zoek })}
              </p>
            ) : (
              <ul className="space-y-2" data-testid="ontdekken-lijst">
                {gevonden.map((item) => {
                  const gekoppeld = itemGekoppeld(item, servers);
                  const eigen = item.soort === EIGEN_ADRES;
                  const niet = item.soort === NIET_KOPPELBAAR;
                  return (
                    <li key={item.id} className="border rounded-md p-3 flex items-start gap-3" data-testid="ontdekken-rij">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium">{item.naam}</p>
                        <p className="text-xs text-muted-foreground">{t(item.omschrijving)}</p>
                        <p className="text-xs text-muted-foreground break-all">
                          {niet ? t("Adres niet gevonden") : eigen ? t("Met eigen adres: jouw server, gevolgd door {pad}", { pad: item.pad }) : item.adres}
                        </p>
                        {item.inloggen && (
                          <p className="text-xs text-muted-foreground" data-testid="ontdekken-soort">
                            {t(INLOG_TEKST[item.inloggen] || INLOG_TEKST.onbekend)} · {t(RECHTEN_TEKST[item.rechten] || RECHTEN_TEKST.onbekend)}
                          </p>
                        )}
                        {item.zeker === false && (
                          <p className="text-xs text-[hsl(var(--destructive))]" data-testid="ontdekken-onzeker">
                            {niet ? t("Niet koppelbaar: {reden}", { reden: item.reden }) : t("Onzeker: {reden}", { reden: item.reden })}
                          </p>
                        )}
                        {eigen && !gekoppeld && magKoppelen(rol) && (
                          <Input value={adressen[item.id] || ""} autoComplete="off"
                                 onChange={(e) => setAdressen((v) => ({ ...v, [item.id]: e.target.value }))}
                                 placeholder={t("Adres van je eigen server")}
                                 aria-label={t("Adres van je eigen {naam}-server", { naam: item.naam })}
                                 className="mt-2 h-8 text-xs" data-testid={`ontdekken-adres-${item.id}`} />
                        )}
                        {item.sleutelNodig && !gekoppeld && magKoppelen(rol) && (
                          <Input type="password" autoComplete="off" value={tokens[item.id] || ""}
                                 onChange={(e) => setTokens((v) => ({ ...v, [item.id]: e.target.value }))}
                                 placeholder={t("Je token")} aria-label={t("Token voor {naam}", { naam: item.naam })}
                                 className="mt-2 h-8 text-xs" data-testid={`ontdekken-token-${item.id}`} />
                        )}
                      </div>
                      {gekoppeld ? (
                        <span className="text-xs flex items-center gap-1" data-testid="ontdekken-gekoppeld">
                          <Check className="h-3 w-3" />{t("Gekoppeld")}
                        </span>
                      ) : magKoppelen(rol) && !niet && (
                        <Button size="sm" variant="outline" disabled={!!bezig} onClick={() => koppel(item)}
                                data-testid="ontdekken-koppel">
                          {bezig === item.id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Link2 className="h-3 w-3 mr-1" />}
                          {t("Koppelen")}
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
